import { defineStore } from 'pinia'

import { computed, ref } from 'vue'

import { ManagementApi } from '@/features/api/services/managementApi.js'
import { getBearerToken, setBearerToken } from '@/features/auth/auth.utils.js'
import { useGetMyDetails } from '@/features/auth/useGetMyDetails.js'
import {
  clearActiveClubId,
  finishClubImpersonation,
  getActiveClubId,
  getIsImpersonation,
} from '@/features/router/useActiveClub.js'
import { useClubRouter } from '@/features/router/useClubRouer.js'

export const useAuthStore = defineStore('authStore', function () {
  const userTokenRef = ref(getBearerToken())
  const myDetails = ref(null)
  const myDetailsLoading = ref(null)
  const myDetailsError = ref(null)

  const avatarUrl = '/images/common/user-avatar.png'

  function setUserToken(token) {
    setBearerToken(token)
    userTokenRef.value = getBearerToken()
  }

  async function logout() {
    clearActiveClubId(null)
    setBearerToken(null)
    finishClubImpersonation(false)
    userTokenRef.value = null
  }

  const isLoggedIn = computed(() => !!userTokenRef.value)

  async function getImpersonatedClub(response) {
    const isImpersonation = getIsImpersonation(response)

    console.log('isImpersonation', isImpersonation)

    if (isImpersonation) {
      const impersonatedClub = await ManagementApi.tenantGetMyDetails()
      if (impersonatedClub?.value) {
        response.myTenants.push(impersonatedClub.value)
      }

      console.log(
        '[useAuthStore] Impersonated club found and added to myTenants:',
        impersonatedClub,
      )
    }

    return response
  }

  async function getMyDetails(setLoading = true) {
    try {
      if (setLoading) {
        myDetailsLoading.value = true
      }

      if (myDetails.value) {
        console.log('Returning cached myDetails')
        return myDetails.value
      }

      const getMyDetails = await useGetMyDetails()

      let response = await getMyDetails()

      // If response is null (network error), return null without processing
      if (response === null) {
        return null
      }

      response = await getImpersonatedClub(response)
      myDetails.value = response

      return response
    } catch (e) {
      console.error('[useAuthStore] getMyDetails error:', {
        error: e,
        message: e?.message,
        response: e?.response,
        status: e?.response?.status,
        data: e?.response?.data,
        code: e?.code,
        stack: e?.stack,
      })
      const activeClubId = getActiveClubId()
      const redirectUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`
      const path = {
        path: '/error',
        query: {
          reason: 'auth-error',
          redirectUrl,
          ...(activeClubId ? { activeClubId } : {}),
        },
      }
      const router = useClubRouter()
      router.push(path)
    } finally {
      myDetailsLoading.value = false
    }
  }

  async function getMyDetailsWithoutCache(setLoading = true) {
    try {
      if (setLoading) {
        myDetailsLoading.value = true
      }

      const getMyDetails = await useGetMyDetails()
      let response = await getMyDetails()
      console.log('getMyDetailsWithoutCache response', response)

      // If response is null (network error), return null without processing
      if (response === null) {
        return null
      }

      response = await getImpersonatedClub(response)
      myDetails.value = response

      return response
    } catch (e) {
      console.error('[useAuthStore] getMyDetailsWithoutCache error:', {
        error: e,
        message: e?.message,
        response: e?.response,
        status: e?.response?.status,
        data: e?.response?.data,
        code: e?.code,
        stack: e?.stack,
      })
      myDetailsError.value = e
    } finally {
      myDetailsLoading.value = false
    }
  }

  const allClubs = computed(() => myDetails.value?.myTenants || [])

  const isImpersonating = computed(() => getIsImpersonation(myDetails.value))

  return {
    setUserToken,
    logout,
    userTokenRef,
    isLoggedIn,

    avatarUrl,

    getMyDetails,
    getMyDetailsWithoutCache,

    myDetailsLoading,
    myDetails,

    allClubs,
    myDetailsError,

    isImpersonating,
  }
})
